<script setup>
import IndexNavbar from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import {ArrowRight, DataLine, FolderOpened} from "@element-plus/icons-vue";

/**
 * 疾病 + 样本数据
 * 点击卡片后跳转到 singlecelldetail
 * query 参数 sample 传递样本名
 */
const diseaseList = [
  {
    title: "Advanced Maternal Age",
    color: "orange",
    samples: ["GSE305088"],
  },
  {
    title: "COVID-19",
    color: "gray",
    samples: ["GSE171381_Dec", "GSE171381_Vil"],
  },
  {
    title: "Early Pregnancy Loss",
    color: "green",
    samples: ["GSE174399"],
  },
  {
    title: "Fetal Growth Restriction",
    color: "blue",
    samples: ["FGR_dataset_1", "GSE314816"],
  },
  {
    title: "Intrahepatic Cholestasis of Pregnancy",
    color: "yellow",
    samples: ["PRJNA949258"],
  },
  {
    title: "Pathogen Infection",
    color: "yellow",
    samples: ["E-MTAB-12795"],
  },
  {
    title: "Placenta Accreta Spectrum Disorders",
    color: "purple",
    samples: ["HRA005072"],
  },
  {
    title: "Preeclampsia",
    color: "red",
    samples: [
      "Med_stroma",
      "Med_TB",
      "GSE183338_Dec",
      "GSE183338_Vil",
      "JustMoveOnnn_Dec",
      "JustMoveOnnn_Pla",
    ],
  },
  {
    title: "Recurrent Implantation Failures",
    color: "pink",
    samples: ["GSE250130", "GSE183837"],
  },
];
</script>

<template>
  <div class="page-wrapper">
    <index-navbar/>

    <section class="main-section">
      <div class="container mx-auto px-4">

        <!-- 面包屑 -->
        <div class="mb-8">
          <el-breadcrumb :separator-icon="ArrowRight" class="breadcrumb">
            <el-breadcrumb-item :to="{ path: '/' }">
              <i class="bi bi-house me-1"></i> Home
            </el-breadcrumb-item>
            <el-breadcrumb-item>Single Cell Disease Atlas</el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <!-- 标题 -->
        <div class="hero-box">
          <h1 class="hero-title">Single Cell Disease Atlas</h1>
          <p class="hero-desc">
            Human pregnancy-related diseases single-cell transcriptome datasets.
            Click any sample below to explore detailed results.
          </p>
        </div>

        <!-- 卡片区域 -->
        <div class="grid-wrapper">
          <div
              v-for="(item, index) in diseaseList"
              :key="index"
              class="disease-card"
          >
            <!-- 左侧标题 -->
            <div class="card-top">
              <div class="icon-box">
                <el-icon>
                  <DataLine/>
                </el-icon>
              </div>

              <div>
                <h3 class="card-title">{{ item.title }}</h3>
              </div>
            </div>

            <!-- 样本 -->
            <div class="sample-area">
              <router-link
                  v-for="(sample, i) in item.samples"
                  :key="i"
                  :to="{
                  name: 'singlecelldetail',
                  query: { sample: sample }
                }"
                  class="sample-btn"
              >
                <el-icon>
                  <FolderOpened/>
                </el-icon>
                {{ sample }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer-component/>
  </div>
</template>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #eef4ff 100%);
}

/* 主体 */
.main-section {
  padding: 130px 0 80px;
}

/* 面包屑 */
.breadcrumb {
  font-size: 18px;
}

/* 顶部介绍 */
.hero-box {
  text-align: center;
  margin-bottom: 50px;
}

.hero-title {
  font-size: 42px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 15px;
}

.hero-desc {
  max-width: 850px;
  margin: auto;
  font-size: 18px;
  line-height: 1.8;
  color: #64748b;
}

/* 网格 */
.grid-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(370px, 1fr));
  gap: 24px;
}

/* 卡片 */
.disease-card {
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(8px);
  border-radius: 22px;
  padding: 24px;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.06);
  transition: all 0.28s ease;
  border: 1px solid rgba(255, 255, 255, 0.6);
}

.disease-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 18px 45px rgba(37, 99, 235, 0.12);
}

/* 顶部 */
.card-top {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.icon-box {
  width: 52px;
  height: 52px;
  min-width: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #2563eb, #60a5fa);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.card-title {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.card-desc {
  color: #64748b;
  line-height: 1.7;
  font-size: 15px;
}

/* 样本区域 */
.sample-area {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

/* 样本按钮 */
.sample-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;
  background: #eff6ff;
  transition: all 0.22s ease;
}

.sample-btn:hover {
  background: #2563eb;
  color: white;
  transform: scale(1.04);
}

/* 手机端 */
@media (max-width: 768px) {
  .hero-title {
    font-size: 30px;
  }

  .hero-desc {
    font-size: 15px;
  }

  .grid-wrapper {
    grid-template-columns: 1fr;
  }

  .card-title {
    font-size: 18px;
  }
}
</style>